import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import { asmrResources, type AsmrResource } from '@/mocks/asmrResources';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const featuredResources = asmrResources.filter((r) => r.featured);
const recentArticles = articles.slice(0, 3);

const categoryImages: Record<string, string> = {
  'zhumian-fangsong': 'https://readdy.ai/api/search-image?query=Moonlit%20peaceful%20bedroom%20with%20soft%20warm%20lighting%2C%20cream%20and%20beige%20tones%2C%20cozy%20sleep%20atmosphere%2C%20minimalist%20decor%2C%20editorial%20photography%2C%20calm%20serene%20mood&width=400&height=400&seq=cat-sleep&orientation=squarish',
  'bai-zaoyin': 'https://readdy.ai/api/search-image?query=Misty%20forest%20with%20soft%20morning%20light%2C%20gentle%20green%20and%20cream%20tones%2C%20peaceful%20nature%2C%20minimalist%20photography%2C%20calm%20serene%20atmosphere&width=400&height=400&seq=cat-noise&orientation=squarish',
  'eryu-asmr': 'https://readdy.ai/api/search-image?query=Warm%20cozy%20recording%20studio%20with%20soft%20lighting%2C%20cream%20beige%20interior%2C%20professional%20microphone%2C%20minimalist%20aesthetic%2C%20peaceful%20atmosphere&width=400&height=400&seq=cat-whisper&orientation=squarish',
  'jiaose-banyan': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20theater%20stage%20with%20soft%20spotlight%2C%20cream%20golden%20tones%2C%20artistic%20atmosphere%2C%20editorial%20photography%2C%20dramatic%20calm%20mood&width=400&height=400&seq=cat-roleplay&orientation=squarish',
  'chibo-jujueyin': 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20food%20scene%20with%20soft%20lighting%2C%20cream%20golden%20beige%20tones%2C%20artisan%20cooking%2C%20editorial%20food%20photography%2C%20clean%20composition&width=400&height=400&seq=cat-eating&orientation=squarish',
  'chufa-yin': 'https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20wooden%20desk%20and%20textured%20objects%2C%20soft%20warm%20lighting%2C%20cream%20beige%20tones%2C%20editorial%20photography%2C%20calm%20focused%20mood&width=400&height=400&seq=cat-trigger&orientation=squarish',
  'zonghe-pingtai': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20digital%20interface%20with%20soft%20warm%20lighting%2C%20cream%20beige%20tones%2C%20modern%20tech%20atmosphere%2C%20editorial%20photography%2C%20calm%20focused%20mood&width=400&height=400&seq=cat-platform&orientation=squarish',
};

function ResourceCard({ resource }: { resource: AsmrResource }) {
  const cat = categories.find((c) => c.slug === resource.category);
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className="group block bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
      data-product-shop
    >
      <div className="relative w-full h-40 overflow-hidden">
        <img
          src={resource.imageUrl}
          alt={resource.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={`${resource.name} - asmr网站推荐`}
        />
        <div className="absolute top-2 right-2 bg-background-50/90 backdrop-blur-sm px-2 py-0.5 rounded-md text-xs font-medium text-primary-700">
          ★ {resource.rating}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors">
          {resource.name}
        </h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
          {resource.description}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {cat && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
              {cat.name}
            </span>
          )}
          {resource.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </a>
  );
}

function CategoryCard({ category }: { category: typeof categories[0]; index: number }) {
  const count = asmrResources.filter((r) => r.category === category.slug).length;
  return (
    <Link
      to={`/category/${category.slug}`}
      className="group flex flex-col items-center p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-accent-200 transition-all duration-300 cursor-pointer"
    >
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300 mb-3">
        <i className={`${category.icon} text-xl`}></i>
      </div>
      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">
        {category.name}
      </h3>
      <p className="text-xs text-foreground-500">{count} 个资源</p>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0]; index: number }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full sm:w-48 h-32 flex-shrink-0 rounded-md overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={article.title}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-400">
          <span>{article.publishDate}</span>
          <span>·</span>
          <span>{article.readTime} 分钟阅读</span>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q2rbknlsngrm4llqig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      const parsed = JSON.parse(responseText);
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ASMR导航',
    url: 'https://asmr-navigation.com',
    description: '最全的ASMR资源导航平台，为ASMR爱好者整理最优质的asmr网站推荐、助眠资源和白噪音平台',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://asmr-navigation.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ASMR导航',
    url: 'https://asmr-navigation.com',
    description: '最全的ASMR资源导航平台',
  };

  return (
    <Layout showBreadcrumb={false}>
      <SEO
        title="ASMR网站精选推荐 — 最全ASMR资源导航"
        description="本页专为ASMR爱好者整理最优质的asmr网站资源，涵盖助眠、放松、白噪音等多种类型频道与平台推荐，帮助你快速找到最适合自己的asmr网站，告别失眠与焦虑，享受沉浸式听觉体验。"
        keywords="asmr网站,asmr网站推荐,最好的asmr网站,asmr助眠网站,asmr在线听,asmr资源平台,asmr白噪音网站"
        canonicalUrl="/"
        schema={{ ...homepageSchema, ...orgSchema }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20abstract%20minimalist%20background%20with%20flowing%20gentle%20curves%20in%20warm%20cream%20and%20soft%20sage%20green%20tones%2C%20dreamy%20atmospheric%20gradient%20with%20subtle%20texture%2C%20calm%20and%20peaceful%20ambient%20mood%2C%20artistic%20composition%20with%20smooth%20color%20transitions%2C%20no%20sharp%20edges%2C%20ethereal%20quality&width=1800&height=900&seq=hero-asmr-main&orientation=landscape"
          alt="ASMR导航 - 最全ASMR资源导航"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="ASMR导航 — 最全ASMR资源导航平台"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
        <div className="relative z-10 w-full max-w-3xl mx-auto text-center px-4">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
            ASMR网站精选推荐 — 最全ASMR资源导航
          </h1>
          <p className="text-sm md:text-base text-white/85 max-w-xl mx-auto leading-relaxed mb-6">
            专为ASMR爱好者整理最优质的<strong>asmr网站</strong>资源，涵盖助眠、放松、白噪音等多种类型，
            帮助你快速找到最适合自己的<strong>asmr助眠网站</strong>，告别失眠与焦虑。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#categories"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              探索分类
            </a>
            <a
              href="#featured"
              className="px-6 py-2.5 bg-background-50/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
            >
              精选推荐
            </a>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section id="featured" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              精选<strong>asmr网站</strong>推荐
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              经过团队精心筛选的优质<strong>asmr资源平台</strong>，每一个都值得你收藏
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
            {featuredResources.slice(0, 8).map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/category/zonghe-pingtai"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
            >
              查看全部资源
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              按分类浏览<strong>asmr网站</strong>
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              七大分类覆盖所有ASMR类型，快速定位你喜欢的<strong>asmr在线听</strong>平台
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat, i) => (
              <CategoryCard key={cat.slug} category={cat} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              最新ASMR文章
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              深度了解ASMR的世界，从入门到精通
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {recentArticles.map((article, i) => (
              <ArticleCard key={article.slug} article={article} index={i} />
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/category/zonghe-pingtai"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
            >
              阅读更多文章
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
            <i className="ri-mail-send-line text-2xl"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            订阅ASMR资源更新
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            每周精选最新<strong>asmr网站推荐</strong>和深度文章，直接发送到你的邮箱
          </p>

          <form
            onSubmit={handleNewsletterSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="anti-bot-field"
            />
            <input
              type="email"
              name="email"
              placeholder="请输入你的邮箱地址"
              required
              className="flex-1 px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
            <button
              type="submit"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即订阅
            </button>
          </form>

          {formStatus === 'success' && (
            <p className="mt-3 text-sm text-accent-600">订阅成功！感谢你的关注。</p>
          )}
          {formStatus === 'error' && (
            <p className="mt-3 text-sm text-red-500">{formError}</p>
          )}
        </div>
      </section>
    </Layout>
  );
}